/**
 * Email content presets.
 *
 * Real insurance-agency email bodies that an operator picks from to seed a
 * new email template, independent of the visual style. Pair a content preset
 * with a letter-shape style in the create-template wizard to get a finished
 * template ready to drop into a workflow.
 *
 * The originals come from the BGI/Allstate "Auto Email Marketing System" cadences
 * (Welcome, X-Date, CPR, etc.). The vendor tokens were converted to this app's
 * merge-tag dialect:
 *   ~Contact.FirstName~        -> {{ firstName }}
 *   ~Contact.LastName~         -> {{ lastName }}
 *   ~Contact.Phone1~           -> {{ phone }}
 *   ~Profile.Signature~        -> rendered by the style as agent/agency block
 *   ~Profile.SalesPhone~       -> {{ agentPhone }}
 *   ~Profile.BusinessName~     -> {{ companyName }}
 *   ~EFS.Name~                 -> {{ efsName }}
 *   ~EFS.Phone.Number~         -> {{ efsPhone }}
 *   ~EFS.Email~                -> {{ efsEmail }}
 *   ~EFS.Bio~                  -> {{ efsBio }}
 *   ~Optional.TollFreePhone~   -> {{ agentPhone }}
 *   ~Optional.ReferralGift~    -> {{ referralGift }}
 */

export interface EmailContentPreset {
  key: string;
  category: string;
  /** Group identifier within a category. Members of the same series share `series`
   * and are ordered by `position`. Used to power "Create whole sequence" later. */
  series?: string;
  position?: number;
  /** Day offset from series start, used as the default delay when bundling into
   * a sequence. The first email in a series has dayOffset 0. */
  dayOffset?: number;
  /** Human-readable cadence hint shown in the picker, e.g. "Immediately",
   * "Day 8", "30 days prior to renewal". */
  cadenceLabel?: string;
  name: string;
  description: string;
  subject: string;
  /** First-line greeting like "Hi {{ firstName }},". Omit for emails that open
   * straight into a banner/headline. */
  greeting?: string;
  /** Body paragraphs in order. */
  paragraphs: string[];
  /** Sign-off line like "Sincerely," "Thanks Again!" — null/undefined skips it. */
  signoff?: string;
  /** Whether to include the auto-rendered agent signature block at the end.
   * Default true. */
  includeSignature?: boolean;
  /** Optional P.S. lines rendered below the signature. */
  ps?: string[];
}

// ---------------------------------------------------------------------------
// Welcome Auto Responder — new customer cadence over 30 days.
// ---------------------------------------------------------------------------

const welcomeDay0: EmailContentPreset = {
  key: "content.welcome.day-0",
  category: "Welcome",
  series: "welcome",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Welcome 1: Thank You",
  description: "Sent immediately after a new policy. Warm, short, no ask.",
  subject: "Thank You For Your Business!",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Thank you very much for allowing our agency to help you with your recent insurance purchase.",
    "We are very excited that you have chosen to do business with our agency. We hope that our relationship continues to grow for many years to come.",
    "If you have any questions, please don't hesitate to give us a call anytime.",
  ],
  signoff: "Thanks again!",
};

const welcomeDay8: EmailContentPreset = {
  key: "content.welcome.day-8",
  category: "Welcome",
  series: "welcome",
  position: 2,
  dayOffset: 8,
  cadenceLabel: "Day 8",
  name: "Welcome 2: Thanks-A-Million",
  description:
    "Week-one check-in. Reassures policy is in motion, names the staff.",
  subject: "Thanks-A-Million for your support of our agency",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Thank you very much for allowing our agency to help you with your recent insurance purchase. You and your business are worth a million dollars to us.",
    "Our insurance coverage specialist in the agency has reviewed your policy to assure accuracy with the quote we provided. You should have already been contacted by our agency if there are any differences in coverage or premium. Our insurance company home office will mail your policy directly to you and you should receive it soon.",
    "We have a dedicated staff here to serve your needs. If you have questions regarding your existing policies, please feel free to contact one of our customer service representatives. If you need to inquire about a new insurance policy or financial product, please ask for one of our licensed insurance agents.",
    "Thank you for choosing us.",
  ],
  signoff: "Sincerely,",
};

const welcomeDay15: EmailContentPreset = {
  key: "content.welcome.day-15",
  category: "Welcome",
  series: "welcome",
  position: 3,
  dayOffset: 15,
  cadenceLabel: "Day 15",
  name: "Welcome 3: Two Weeks In",
  description: "Two-week follow-up. Confirms policy delivery, offers help.",
  subject: "Thanks again for your business",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Thanks again for allowing us to help you with your recent insurance purchase two weeks ago.",
    "By now you should have received your insurance policy from the home office. If not, feel free to reply back and we will check on it for you.",
    "We have a dedicated staff here to serve your needs. If you have questions regarding your existing policies, please feel free to contact one of our customer service representatives. If you need to inquire about a new insurance policy or financial product, please ask for one of our licensed insurance agents.",
  ],
  signoff: "Sincerely,",
};

const welcomeDay30: EmailContentPreset = {
  key: "content.welcome.day-30",
  category: "Welcome",
  series: "welcome",
  position: 4,
  dayOffset: 30,
  cadenceLabel: "Day 30",
  name: "Welcome 4: 30-Day Anniversary",
  description: "One-month check-in. Light touch, opens the door to more help.",
  subject: "Happy Anniversary",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "It's been 30 days since we wrote your insurance policy for you and we just wanted to thank you again for your business.",
    "I really appreciate the opportunity to handle this for you, and I was just checking in to see if there was anything else I could do for you.",
  ],
  signoff: "Sincerely,",
};

// ---------------------------------------------------------------------------
// EFS Intro Welcome — same cadence but email 2 introduces the Financial
// Specialist. Authored as a parallel series so the operator picks one or the
// other rather than mixing.
// ---------------------------------------------------------------------------

const efsWelcomeDay0: EmailContentPreset = {
  key: "content.efs-welcome.day-0",
  category: "Welcome",
  series: "efs-welcome",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "EFS Welcome 1: Thank You",
  description:
    "Same opener as the standard welcome; series introduces an EFS rep on day 8.",
  subject: "Thank You For Your Business!",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Thank you very much for allowing our agency to help you with your recent insurance purchase.",
    "We are very excited that you have chosen to do business with our agency. We hope that our relationship continues to grow for many years to come.",
    "If you have any questions, please don't hesitate to give us a call anytime.",
  ],
  signoff: "Thanks again!",
};

const efsWelcomeDay8: EmailContentPreset = {
  key: "content.efs-welcome.day-8",
  category: "Welcome",
  series: "efs-welcome",
  position: 2,
  dayOffset: 8,
  cadenceLabel: "Day 8",
  name: "EFS Welcome 2: Financial Specialist Intro",
  description: "Hands the customer off to the agency's Financial Specialist.",
  subject: "Thanks-A-Million for your support of our agency",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Thank you very much for allowing our agency to help you with your recent insurance purchase. You and your business are worth a million dollars to us.",
    "Our insurance coverage specialist in the agency has reviewed your policy to assure accuracy with the quote we provided. You should have already been contacted by our agency if there are any differences in coverage or premium. Our insurance company home office will mail your policy directly to you and you should receive it soon.",
    "As part of my agency's commitment to our customers, we'd like you to meet with our Financial Specialist, {{ efsName }}, who has helped many of our customers.",
    "Please let {{ efsName }} know what your schedule looks like in the next few weeks so we can set up an appointment. If you'd like to call {{ efsName }} directly, the number is {{ efsPhone }}, or you can email at {{ efsEmail }}. As always, thank you for your business and referrals.",
  ],
  signoff: "Sincerely,",
};

const efsWelcomeDay15: EmailContentPreset = {
  ...welcomeDay15,
  key: "content.efs-welcome.day-15",
  series: "efs-welcome",
  name: "EFS Welcome 3: Two Weeks In",
};

const efsWelcomeDay30: EmailContentPreset = {
  ...welcomeDay30,
  key: "content.efs-welcome.day-30",
  series: "efs-welcome",
  name: "EFS Welcome 4: 30-Day Anniversary",
};

// ---------------------------------------------------------------------------
// X-Date — renewal-window drip starting 60 days before renewal, six emails.
// All have urgent, rate-decrease-focused copy and a referral-gift hook.
// ---------------------------------------------------------------------------

const xdateDay60: EmailContentPreset = {
  key: "content.xdate.day-60",
  category: "X-Date",
  series: "xdate",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "60 days prior to renewal",
  name: "X-Date 1: Slashed Rates",
  description: "First touch on the x-date; reintroduces last year's quote.",
  subject: "Since we last spoke we have SLASHED our rates",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "I understand that it should be about time for your insurance to renew. Last year I quoted your home and/or auto insurance and I wanted to follow up with you on the quotes we discussed.",
    "Our agency, {{ companyName }}, has recently SLASHED insurance rates and added several NEW DISCOUNTS, and we may be able to save you HUGE money on your insurance now.",
    "Just hit the reply button and let me know when a good time to talk would be. Or call me now at {{ agentPhone }} or toll free at {{ agentPhone }} with any updates you may have had since we last spoke, and I'll be more than happy to update the quotes in our system for you.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call my direct number now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

const xdateDay45: EmailContentPreset = {
  key: "content.xdate.day-45",
  category: "X-Date",
  series: "xdate",
  position: 2,
  dayOffset: 15,
  cadenceLabel: "45 days prior to renewal",
  name: "X-Date 2: Gift Waiting",
  description: "Two-week follow-up. Leads with the referral gift.",
  subject: "We have your {{ referralGift }} waiting for you",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "A couple of weeks ago I sent you a message that it should be about time for your insurance to renew. Last year I quoted your home and/or auto insurance and I wanted to follow up with you on the quotes we discussed.",
    "Our agency, {{ companyName }}, has recently SLASHED insurance rates and added several NEW DISCOUNTS, and we may be able to save you HUGE money on your insurance now.",
    "Just hit the reply button and let me know when a good time to talk would be. Or call me now at {{ agentPhone }} or toll free at {{ agentPhone }} with any updates you may have had since we last spoke, and I'll be more than happy to update the quotes in our system for you.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call me now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

const xdateDay30: EmailContentPreset = {
  key: "content.xdate.day-30",
  category: "X-Date",
  series: "xdate",
  position: 3,
  dayOffset: 30,
  cadenceLabel: "30 days prior to renewal",
  name: "X-Date 3: Are You Ready to Save",
  description:
    "Longest email of the cadence. Removes friction with 'we'll do it for you' framing.",
  subject: "Are you ready to SAVE some HUGE money",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "A couple of weeks ago I sent you another message that it should be about time for your insurance to renew. Last year I quoted your home and/or auto insurance and I wanted to follow up with you on the quotes we discussed.",
    "Our agency, {{ companyName }}, has recently SLASHED insurance rates and added several NEW DISCOUNTS, and we may be able to save you HUGE money on your insurance now.",
    "Frankly, {{ firstName }}, I can think of only one reason why you wouldn't immediately contact me back to start saving money on your home and/or auto insurance and enjoy the nationally recognized customer satisfaction that we offer.",
    "You're too busy. My guess is you realize this is absolutely the right thing to do, but you just don't have the time to mess with it.",
    "Well, because you're already in our system, I've made it super-easy for you.",
    "I am your personal licensed insurance advisor and will be here to manage the entire process over the phone or by email. No trying to wedge an appointment into your already jammed schedule. You'll never have to leave the house. (Of course, if you want to come into the office, you're more than welcome.)",
    "Here's what to do. There are 2 convenient ways to contact me: call me now at {{ agentPhone }} or toll free at {{ agentPhone }}, or just hit the reply button and let me know when a good time to talk would be, or email over your current coverages and any updates we may need to make.",
    "I will be your personal risk manager. We can work out the easiest, most convenient way for getting this done fast and hassle-free.",
    "Remember, the sooner you act, the sooner you save, so there's no time to waste. Every day you wait is another day you are overpaying on your insurance.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call me now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

const xdateDay20: EmailContentPreset = {
  key: "content.xdate.day-20",
  category: "X-Date",
  series: "xdate",
  position: 4,
  dayOffset: 40,
  cadenceLabel: "20 days prior to renewal",
  name: "X-Date 4: Rates Slashed Follow-Up",
  description:
    "Mentions the mailed quote packet; pushes for an updated info call.",
  subject: "Our rates have been slashed",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "I didn't hear from you in regards to my last email I sent about 10 days ago regarding the quotes for your home and/or auto insurance, and I wanted to follow up with you again.",
    "Our agency, {{ companyName }}, has taken some HUGE rate decreases and added several NEW discounts since the last time we spoke. I didn't want you to miss out on the chance to save some money on your insurance this year.",
    "I mailed you an updated copy of the quotes that we have saved in our system. However, I'm sure some of your information has changed since we last spoke, so I'd love to review the information with you and make sure that we have everything correct.",
    "Just hit the reply button and let me know when a good time to talk would be. Or call me now at {{ agentPhone }} or toll free at {{ agentPhone }} with any updates you may have had since we last spoke, and I'll be more than happy to update the quotes in our system for you.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call me now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

const xdateDay15: EmailContentPreset = {
  key: "content.xdate.day-15",
  category: "X-Date",
  series: "xdate",
  position: 5,
  dayOffset: 45,
  cadenceLabel: "15 days prior to renewal",
  name: "X-Date 5: Gift Reminder",
  description:
    "Mirrors X-Date 4 with the referral-gift framing back in the subject.",
  subject: "We have your {{ referralGift }} waiting for you",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "I didn't hear from you in regards to my last email I sent about 5 days ago regarding the quotes for your home and/or auto insurance, and I wanted to follow up with you again.",
    "Our agency, {{ companyName }}, has taken some HUGE rate decreases and added several NEW discounts since the last time we spoke. I didn't want you to miss out on the chance to save some money on your insurance this year.",
    "I mailed you an updated copy of the quotes that we have saved in our system. However, I'm sure some of your information has changed since we last spoke, so I'd love to review the information with you and make sure that we have everything correct.",
    "Just hit the reply button and let me know when a good time to talk would be. Or call me now at {{ agentPhone }} or toll free at {{ agentPhone }} with any updates you may have had since we last spoke, and I'll be more than happy to update the quotes in our system for you.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call me now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

const xdateDay10: EmailContentPreset = {
  key: "content.xdate.day-10",
  category: "X-Date",
  series: "xdate",
  position: 6,
  dayOffset: 50,
  cadenceLabel: "10 days prior to renewal",
  name: "X-Date 6: Last Chance",
  description:
    "Final push. Acknowledges they didn't move last year; offers fresh review.",
  subject: "Last chance to save HUGE on your auto/home insurance",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "I had a reminder in my calendar to get back with you regarding your home and/or auto insurance. When we last spoke, we were not able to help you. However, since then we have taken a HUGE rate decrease and would like to review your information and update any necessary changes.",
    "Just hit the reply button and let me know when a good time to talk would be. Or call me now at {{ agentPhone }} or toll free at {{ agentPhone }} with any updates you may have had since we last spoke, and I'll be more than happy to update the quotes in our system for you.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
  signoff: "Thank you,",
  ps: [
    "Just hit the reply button or call me now at {{ agentPhone }} to start saving RIGHT AWAY.",
    "I am so confident you will switch your insurance to us that I will give you a {{ referralGift }} just to call and talk to me about it.",
  ],
};

// ---------------------------------------------------------------------------
// CPR — Customer Protection Review push. Three emails, designed to be scheduled
// either before renewal (30/20/10 days prior) or as a standalone 30-day cadence.
// ---------------------------------------------------------------------------

const cprFirst: EmailContentPreset = {
  key: "content.cpr.first",
  category: "CPR",
  series: "cpr",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately (or 30 days prior to renewal)",
  name: "CPR 1: I Need To Talk To You",
  description: "Direct ask for a phone call. No softening, no setup.",
  subject: "Hi {{ firstName }}, I need to talk to you",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "We need to speak with you right away regarding your insurance. Please call the agency today at {{ agentPhone }}, or reply back to this email and let me know a good time to reach you.",
  ],
  signoff: "Best regards,",
};

const cprSecond: EmailContentPreset = {
  key: "content.cpr.second",
  category: "CPR",
  series: "cpr",
  position: 2,
  dayOffset: 10,
  cadenceLabel: "Day 10 (or 20 days prior to renewal)",
  name: "CPR 2: Did You See My Last Email",
  description: "Follow-up on the first email; gentle reminder.",
  subject: "Hi {{ firstName }}, I need to talk to you",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "About a week ago, I sent you an email that we needed to speak with you right away regarding your insurance. Please call the agency today at {{ agentPhone }}, or reply back to this email and let me know a good time to reach you.",
    "If you've already spoken to me or one of my staff, please disregard this reminder.",
  ],
  signoff: "Best regards,",
};

const cprThird: EmailContentPreset = {
  key: "content.cpr.third",
  category: "CPR",
  series: "cpr",
  position: 3,
  dayOffset: 20,
  cadenceLabel: "Day 20 (or 10 days prior to renewal)",
  name: "CPR 3: Final Reminder",
  description:
    "Last touch in the cadence. Acknowledges persistence, names the impact.",
  subject: "Have we talked yet?",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "I don't mean to be a pest, and if we have already spoken, thank you for replying and please disregard this message.",
    "We really need to talk about your insurance as it affects how much you pay on your insurance policies.",
    "This is the third and final time I will reach out to you. We need to speak with you regarding your insurance ASAP. Please call the agency today at {{ agentPhone }}, or reply back to this email and let me know a good time to reach you.",
  ],
  signoff: "Best regards,",
};

// ---------------------------------------------------------------------------
// New Business Online Quote — follow-up after a customer submits an online form
// requesting a quote. Three emails over the first week.
// ---------------------------------------------------------------------------

const nbOnlineDay0: EmailContentPreset = {
  key: "content.nb-online.day-0",
  category: "New Quote",
  series: "nb-online",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately after online quote request",
  name: "New Online Quote 1: Thanks For Submitting",
  description:
    "Auto-replies to an online quote submission. Names that a quote was sent separately.",
  subject: "Thank you for your quote request",
  greeting: "Hello {{ firstName }},",
  paragraphs: [
    "Thank you very much for taking the time to submit your information for an insurance quote with our agency.",
    "Earlier, I sent you an email quote, and I'd be delighted to answer any specific questions or concerns, as well as tell you about the many special services we offer.",
    "Just hit the reply button with your questions or call me at the number below.",
  ],
  signoff: "All the best,",
};

const nbOnlineDay3: EmailContentPreset = {
  key: "content.nb-online.day-3",
  category: "New Quote",
  series: "nb-online",
  position: 2,
  dayOffset: 3,
  cadenceLabel: "Day 3",
  name: "New Online Quote 2: Day 3 Check-In",
  description: "Soft follow-up. Includes the original quote email as a P.S.",
  subject: "Just checking in on your quote",
  greeting: "Hello {{ firstName }},",
  paragraphs: [
    "You've had a couple of days to sleep and think about the quote we emailed you regarding your insurance protection.",
    "Just checking in with you to see if there was anything else I could do for you.",
  ],
  signoff: "Sincerely,",
  ps: [
    "Below is the original email we sent a couple of days ago. Thank you very much for taking the time to submit your information for an insurance quote with our agency. Earlier, I sent you an email quote, and I'd be delighted to answer any specific questions or concerns, as well as tell you about the many special services we offer. Just hit the reply button with your questions or call me at the number below.",
  ],
};

const nbOnlineDay5: EmailContentPreset = {
  key: "content.nb-online.day-5",
  category: "New Quote",
  series: "nb-online",
  position: 3,
  dayOffset: 5,
  cadenceLabel: "Day 5",
  name: "New Online Quote 3: Day 5 Close",
  description: "Final touch in the cadence. Empathy framing, no pressure.",
  subject: "Thanks again for considering us",
  paragraphs: [
    "Thanks again for contacting me regarding your insurance. I've seen firsthand how not having proper coverage can cause financial hardship to your family. I really appreciate the opportunity to handle this for you. Have a great day.",
  ],
  signoff: "Sincerely,",
};

// ---------------------------------------------------------------------------
// New Business Quote (phone) — same cadence but assumes a phone conversation
// happened first rather than an online submission.
// ---------------------------------------------------------------------------

const nbPhoneDay0: EmailContentPreset = {
  key: "content.nb-phone.day-0",
  category: "New Quote",
  series: "nb-phone",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately after phone quote",
  name: "New Phone Quote 1: Nice To Connect",
  description: "Right after the phone call. Drops your contact info, no ask.",
  subject: "Great to speak with you",
  greeting: "Hello {{ firstName }},",
  paragraphs: [
    "I'm glad you spoke to our agency about your insurance. I just wanted to drop you a note so you'd have my contact information.",
    "If you have any questions, or think of any, just let me know.",
  ],
};

const nbPhoneDay3: EmailContentPreset = {
  key: "content.nb-phone.day-3",
  category: "New Quote",
  series: "nb-phone",
  position: 2,
  dayOffset: 3,
  cadenceLabel: "Day 3",
  name: "New Phone Quote 2: Day 3 Check-In",
  description: "Light touch after a couple of days to think.",
  subject: "Just checking in",
  paragraphs: [
    "I know you've had a couple of days to sleep and think about our conversation on your insurance protection.",
    "Just checking in with you to see if there was anything else I could do for you.",
  ],
  signoff: "Sincerely,",
};

const nbPhoneDay5: EmailContentPreset = {
  key: "content.nb-phone.day-5",
  category: "New Quote",
  series: "nb-phone",
  position: 3,
  dayOffset: 5,
  cadenceLabel: "Day 5",
  name: "New Phone Quote 3: Day 5 Close",
  description: "Last email in the cadence.",
  subject: "Thanks again for the conversation",
  paragraphs: [
    "Thanks again for contacting me regarding your insurance. I've seen firsthand how not having proper coverage can cause financial hardship to your family. I really appreciate the opportunity to handle this for you. Have a great day.",
  ],
  signoff: "Sincerely,",
};

// ---------------------------------------------------------------------------
// Thank You Kit — for existing customers who just bought additional coverage.
// Mirrors the welcome cadence but assumes a prior relationship.
// ---------------------------------------------------------------------------

const thankYouDay0: EmailContentPreset = {
  key: "content.thank-you.day-0",
  category: "Thank You",
  series: "thank-you-existing",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Thank You 1: Existing Customer",
  description: "Sent right after an existing customer buys more coverage.",
  subject: "Thank You For Your Business!",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Thank you very much for your continued trust and support of our agency with your recent purchase in protection for yourself and your family.",
    "We hope that our relationship continues to grow for many years to come.",
    "If you have any questions, don't hesitate to give us a call anytime. Please let me know how we can better serve you now and in the years to come.",
  ],
  signoff: "Thanks again!",
};

const thankYouDay8: EmailContentPreset = {
  key: "content.thank-you.day-8",
  category: "Thank You",
  series: "thank-you-existing",
  position: 2,
  dayOffset: 8,
  cadenceLabel: "Day 8",
  name: "Thank You 2: Thanks-A-Million",
  description:
    "Week-one check-in for existing customers; confirms accuracy of the new coverage.",
  subject: "Thanks-A-Million for your support of our agency",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Thank you very much for allowing our agency to help you with your recent insurance purchase in protection for yourself or your family. You and your business are worth a million dollars to us.",
    "It has been a week since your new purchase. Our insurance coverage specialist in the agency has reviewed your policy to assure accuracy with the quote we provided. You should have already been contacted by our agency if there are any differences in coverage or premium. Our insurance company home office will mail your policy directly to you and you should receive it soon.",
    "We have a dedicated staff here to serve your needs. If you have questions regarding your existing policies, please feel free to contact one of our customer service representatives. If you need to inquire about a new insurance policy or financial product, please ask for one of our licensed insurance agents.",
    "Thank you for your continued support.",
  ],
  signoff: "Sincerely,",
};

const thankYouDay15: EmailContentPreset = {
  key: "content.thank-you.day-15",
  category: "Thank You",
  series: "thank-you-existing",
  position: 3,
  dayOffset: 15,
  cadenceLabel: "Day 15",
  name: "Thank You 3: Two Weeks In",
  description: "Two-week follow-up to confirm policy delivery.",
  subject: "Thanks again for your continued business",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Thanks again for allowing us to help you with your recent insurance purchase two weeks ago.",
    "By now you should have received your insurance policy from the home office. If not, feel free to reply back and we will check on it for you.",
    "We have a dedicated staff here to serve your needs. If you have questions regarding your existing policies, please feel free to contact one of our customer service representatives. If you need to inquire about a new insurance policy or financial product, please ask for one of our licensed insurance agents.",
  ],
  signoff: "Sincerely,",
};

// ---------------------------------------------------------------------------
// Endorsement — confirms a policy change. Two variants: generic + Allstate
// (which folds in the customer-satisfaction survey ask).
// ---------------------------------------------------------------------------

const endorsementGeneric: EmailContentPreset = {
  key: "content.endorsement.generic",
  category: "Endorsement",
  name: "Endorsement: Generic",
  description:
    "Short policy-change confirmation. One-off, not part of a series.",
  subject: "{{ firstName }}, important information about your policy change",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "Your recent change to your policy has been completed. You should receive verification from the home office via mail or email confirming the change.",
    "If you have any questions about the information you receive, call the office at {{ agentPhone }} to speak with my staff. They'll be happy to help.",
    "Again, thank you for being a valued customer. I'm proud to be your agent, and I appreciate your continued business.",
  ],
  signoff: "Professionally yours,",
};

const endorsementAllstate: EmailContentPreset = {
  key: "content.endorsement.allstate-survey",
  category: "Endorsement",
  name: "Endorsement: Allstate (with survey ask)",
  description:
    "Policy-change confirmation that primes the customer for the Allstate satisfaction survey.",
  subject: "{{ firstName }}, important information about your policy change",
  greeting: "Hi {{ firstName }},",
  paragraphs: [
    "Your recent change to your policy has been completed. You should receive verification from Allstate home office via mail or email confirming the change.",
    "If you have any questions about the information you receive, call the office at {{ agentPhone }} to speak with my staff. They'll be happy to help.",
    'By the way, you may be receiving an email survey from our corporate offices with the subject line "Allstate Needs Your Help." By completing the survey, you\'ll be completing a report card for my agency and can enter to win $500 cash.',
    "This survey is how our agency is graded. It is my hope that you'll be able to answer the survey by giving us a score of 9 or 10, which states that you're \"completely satisfied\" with our agency. Our agency staff strives to be the best that we can be and we are anxiously awaiting this agency survey.",
    "We recognize that there are things that we can control and those which we cannot, and we hope that you recognize this as well. Please keep in mind that we do not control rates and claims procedures. These are handled at the corporate level.",
    "We do control the service our agency delivers, such as handling requests, coverage offerings, periodic policy coverage reviews, and providing you with all needed information, including rate changes, to make well-informed decisions.",
    "If you would be unable to provide us with a score of 9 or 10, then please contact us immediately to discuss how we may improve our service.",
    "Again, thank you for being a valued customer. I'm proud to be your agent, and I appreciate your continued business.",
  ],
  signoff: "Professionally yours,",
};

// ---------------------------------------------------------------------------
// Claims — sympathetic acknowledgment + 7-day follow-up.
// ---------------------------------------------------------------------------

const claimsFiled: EmailContentPreset = {
  key: "content.claims.filed",
  category: "Claims",
  series: "claims",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately after claim filed",
  name: "Claims 1: Sorry to Hear",
  description: "Empathetic short note when a claim is opened.",
  subject: "Sorry to hear about your claim",
  greeting: "{{ firstName }},",
  paragraphs: [
    "We are sorry to hear about your recent insurance claim.",
    "I dropped some important information in the mail to you today.",
    "If you have any questions or concerns, please email or call me.",
  ],
  signoff: "Best regards,",
};

const claimsFollowup: EmailContentPreset = {
  key: "content.claims.followup-7d",
  category: "Claims",
  series: "claims",
  position: 2,
  dayOffset: 7,
  cadenceLabel: "Day 7",
  name: "Claims 2: Just Checking In",
  description: "Week-later check-in. Holds the door open for questions.",
  subject: "{{ firstName }}, just touching base with you",
  greeting: "{{ firstName }},",
  paragraphs: [
    "I wanted to see if you had any questions, concerns, or comments about your recent insurance claim.",
    "I hope everything went well or is going well with your claim.",
  ],
  signoff: "All the best,",
};

// ---------------------------------------------------------------------------
// Win Back — for customers who have left the agency.
// ---------------------------------------------------------------------------

const winbackDay0: EmailContentPreset = {
  key: "content.winback.day-0",
  category: "Win Back",
  series: "winback",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Win Back 1: We Want You Back",
  description: "Direct opener. All caps, urgency, named price hook.",
  subject: "We would like to earn back your business",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "WE WOULD LIKE TO EARN BACK YOUR BUSINESS",
    "Since you left our agency, insurance rates have been... SLASHED.",
    "Contact me today for your free quote. Every day you wait is another day you overpay. Call us now at {{ agentPhone }}.",
  ],
  signoff: "Sincerely,",
};

const winbackDay3: EmailContentPreset = {
  key: "content.winback.day-3",
  category: "Win Back",
  series: "winback",
  position: 2,
  dayOffset: 3,
  cadenceLabel: "Day 3",
  name: "Win Back 2: Have You Had Time",
  description: "Short follow-up after the first email.",
  subject: "Have you had time to think it over?",
  paragraphs: [
    "I know you have had a couple of days to sleep and think about the email I sent you regarding your insurance protection. Just checking in with you to see if there was anything I could do for you.",
  ],
  signoff: "Sincerely,",
};

const winbackDay5: EmailContentPreset = {
  key: "content.winback.day-5",
  category: "Win Back",
  series: "winback",
  position: 3,
  dayOffset: 5,
  cadenceLabel: "Day 5",
  name: "Win Back 3: Final Touch",
  description: "Last email in the cadence. Empathy framing, no pressure.",
  subject: "Just wanted to reach out one more time",
  paragraphs: [
    "{{ firstName }}, I've seen firsthand how not having proper coverage can cause financial hardships to you and your family. I really appreciate the opportunity to handle this for you. If you have any questions, just send us an email or call the number below. Have a great day.",
  ],
};

// ---------------------------------------------------------------------------
// Birthday — single-email annual greeting fired on the customer's birthday.
// ---------------------------------------------------------------------------

const birthdayDay0: EmailContentPreset = {
  key: "content.birthday.day-0",
  category: "Birthday",
  series: "birthday",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "On the customer's birthday",
  name: "Birthday Greeting",
  description: "Warm, no-ask birthday note signed by the agent.",
  subject: "Happy birthday, {{ firstName }}",
  greeting: "Dear {{ firstName }},",
  paragraphs: [
    "Just a quick note to wish you a very happy birthday from everyone at {{ companyName }}. I hope today brings everything you love, like good food, the people who matter most, and a little time for yourself.",
    "Thank you for letting us look after your insurance. We genuinely appreciate the trust you place in us, and it's a privilege to have you as a customer.",
    "Wishing you a wonderful day and a great year ahead.",
  ],
  signoff: "Warmly,",
};

// ---------------------------------------------------------------------------
// Seasonal — single-touch sends timed to the calendar.
// ---------------------------------------------------------------------------

const holidayGreetingDay0: EmailContentPreset = {
  key: "content.holiday-greeting.day-0",
  category: "Seasonal",
  series: "holiday-greeting",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Holiday Greeting",
  description:
    "Year-end thank-you note. Works for Thanksgiving, Christmas, and New Year.",
  subject: "A warm note from {{ companyName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "As the year winds down, I just wanted to take a minute to say thank you. Being trusted to look after your insurance is something I don't take for granted, and I'm grateful you chose to work with us.",
    "Whatever you're celebrating this season, I hope it's a peaceful one, surrounded by the people you care about, with a little time to slow down.",
    "If anything has changed in your life or your coverage needs this past year, we'd love to do a quick review when you're ready. No pressure, just here when it's useful.",
  ],
  signoff: "Warmly,",
};

const hurricanePrepDay0: EmailContentPreset = {
  key: "content.hurricane-prep.day-0",
  category: "Seasonal",
  series: "hurricane-prep",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Severe-Weather Prep",
  description:
    "Storm-season reminder with claim hotline. Fires once at the start of weather season.",
  subject: "A quick weather-prep reminder from {{ companyName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Severe-weather season is here, and we want to make sure you're prepared in case a storm rolls through our area.",
    "A few things worth doing now: take photos or video of every room of your home and store them somewhere you can reach from your phone, keep a copy of your policy and your claim hotline number where you can grab it fast, and clear gutters and outdoor furniture before any warning hits.",
    "If a storm does cause damage, you can reach our team at {{ agentPhone }}. We'll walk you through what to do, what to document, and what to expect.",
    "Hopefully you don't need any of this. But it's a good few minutes spent either way.",
  ],
  signoff: "Stay safe,",
};

const taxSeasonDay0: EmailContentPreset = {
  key: "content.tax-season.day-0",
  category: "Seasonal",
  series: "tax-season",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Tax-Season Check-In",
  description:
    "Light-touch outreach during W-2/1099 season with a hook to review life and disability coverage.",
  subject: "A small thought for tax season, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Tax season has a way of pulling everyone's finances out into the open. While you're gathering your W-2s and 1099s, it's actually a good moment to make sure your insurance is keeping up with the rest of your life.",
    "A few questions worth thinking about: Did your income change last year? Did you take on new debt, like a house or a car? Anyone new depending on your income, like a spouse, a child, or a parent?",
    "If the answer to any of those is yes, your life or disability coverage might be undersized for the life you have now. No charge to find out, and a 15-minute review is all it takes.",
    "If you'd like, hit reply or call {{ agentPhone }} and we'll find a time.",
  ],
  signoff: "Talk soon,",
};

const backToSchoolDay0: EmailContentPreset = {
  key: "content.back-to-school.day-0",
  category: "Seasonal",
  series: "back-to-school",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Back-to-School Coverage Check",
  description:
    "August reminder covering teen drivers, dorm renters insurance, and college-student auto re-rates.",
  subject: "Back-to-school coverage check, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Back-to-school season is a good prompt to double-check a few coverage things that quietly change when the kids head out the door.",
    "Three quick ones worth a look: if you have a new teen driver, getting them properly listed on your auto policy now beats finding out the hard way later. If a student is moving to a dorm or off-campus, a renters policy is usually under $20 a month and protects their laptop, bike, and gear. And if a college student drives a car only occasionally, a re-rate can sometimes lower the premium.",
    "If any of those are on your mind, reply to this email or call us at {{ agentPhone }} and we'll sort it out in a few minutes.",
  ],
  signoff: "Best,",
};

const newYearDay0: EmailContentPreset = {
  key: "content.new-year.day-0",
  category: "Seasonal",
  series: "new-year",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "New Year",
  description: "Warm new-year note with a soft hook to review coverage.",
  subject: "A fresh start from {{ companyName }}, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Happy New Year. Whatever last year held, we hope this one brings more of the good stuff and a lot less of everything else.",
    "Quick thought for January: it's a great month to dust off your policies and make sure your coverage matches the life you're heading into this year. If anything changed, like a new car, a move, or a new family member, we're happy to take a fresh look.",
    "No urgency. Just here when it's useful.",
  ],
  signoff: "Happy New Year,",
};

const valentinesDay0: EmailContentPreset = {
  key: "content.valentines.day-0",
  category: "Seasonal",
  series: "valentines",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Valentine's Day",
  description: "Short, warm Valentine's Day note with no ask.",
  subject: "Thinking of you and yours, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "A short note this Valentine's Day to say thanks for being part of our agency. The people we look after aren't just policy numbers, and we don't take that for granted.",
    "We hope today's a warm one for you and the people who matter most.",
  ],
  signoff: "Warmly,",
};

const stPatricksDay0: EmailContentPreset = {
  key: "content.st-patricks.day-0",
  category: "Seasonal",
  series: "st-patricks",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "St. Patrick's Day",
  description:
    "Friendly St. Patrick's Day note with a safe-ride-home reminder.",
  subject: "A small note this St. Patrick's Day, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Happy St. Patrick's Day. Whether you're celebrating big or just enjoying the day, here's a quick reminder from {{ companyName }}: if you're heading out tonight, please plan a safe ride home.",
    "A claim is the last thing anyone wants to start their week with. If anything does come up, you know where to find us.",
  ],
  signoff: "Stay safe,",
};

const easterDay0: EmailContentPreset = {
  key: "content.easter.day-0",
  category: "Seasonal",
  series: "easter",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Easter",
  description: "Brief, warm Easter note.",
  subject: "A warm Easter note, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Just a quick note from {{ companyName }} to wish you and your family a happy Easter.",
    "However you spend the day, whether quietly, loudly, with a packed dining room or just a quiet brunch, we hope it's a good one.",
  ],
  signoff: "Warmly,",
};

const mothersDay0: EmailContentPreset = {
  key: "content.mothers-day.day-0",
  category: "Seasonal",
  series: "mothers-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Mother's Day",
  description: "Warm Mother's Day note.",
  subject: "Happy Mother's Day, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Wishing you and the mothers in your life a wonderful Mother's Day from everyone at {{ companyName }}.",
    "Whether you're being celebrated or doing the celebrating, we hope today is a warm one.",
  ],
  signoff: "With gratitude,",
};

const memorialDay0: EmailContentPreset = {
  key: "content.memorial-day.day-0",
  category: "Seasonal",
  series: "memorial-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Memorial Day",
  description:
    "Memorial Day remembrance plus a safe-travels note for the long weekend.",
  subject: "Remembering this Memorial Day, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "A quiet note this Memorial Day to remember the men and women who gave everything in service. We're grateful every day, but especially today.",
    "And if your weekend takes you out on the road, please drive safe out there. We'll be here if you need us.",
  ],
  signoff: "With respect,",
};

const fathersDay0: EmailContentPreset = {
  key: "content.fathers-day.day-0",
  category: "Seasonal",
  series: "fathers-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Father's Day",
  description: "Warm Father's Day note.",
  subject: "Happy Father's Day, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Wishing you and the dads in your life a great Father's Day from everyone at {{ companyName }}.",
    "Whether it's a steakhouse afternoon, a backyard with the grill going, or a couch and a good game, we hope today brings you a little peace.",
  ],
  signoff: "Warmly,",
};

const independenceDay0: EmailContentPreset = {
  key: "content.independence-day.day-0",
  category: "Seasonal",
  series: "independence-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Independence Day",
  description: "Friendly 4th of July note with a safe-celebration reminder.",
  subject: "Happy 4th of July, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "From all of us at {{ companyName }}, have a happy and safe 4th of July.",
    "A quick reminder before the weekend kicks off: if you're heading out on the water, on the road, or anywhere with fireworks, please be careful out there. A small bit of caution goes a long way.",
    "And if anything does come up, we're a phone call away at {{ agentPhone }}.",
  ],
  signoff: "Stay safe,",
};

const laborDay0: EmailContentPreset = {
  key: "content.labor-day.day-0",
  category: "Seasonal",
  series: "labor-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Labor Day",
  description: "Brief end-of-summer Labor Day note.",
  subject: "A long-weekend note, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Labor Day is a good moment to slow down and recognize the work that quietly keeps everything moving, including yours.",
    "From all of us at {{ companyName }}, thank you. Have a restful long weekend.",
  ],
  signoff: "Warmly,",
};

const halloweenDay0: EmailContentPreset = {
  key: "content.halloween.day-0",
  category: "Seasonal",
  series: "halloween",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Halloween",
  description: "Fun Halloween note with a drive-slow reminder.",
  subject: "A quick Halloween note, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Happy Halloween from {{ companyName }}. Whether you're handing out candy at the door, taking kids out trick-or-treating, or dodging the whole thing entirely, have a fun, safe one.",
    "Quick reminder: drive slow in neighborhoods tonight. Lots of small ghosts and superheroes out there.",
  ],
  signoff: "Stay safe,",
};

const veteransDay0: EmailContentPreset = {
  key: "content.veterans-day.day-0",
  category: "Seasonal",
  series: "veterans-day",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Veterans Day",
  description: "Thank-you note for Veterans Day.",
  subject: "Thank you this Veterans Day, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "A short note this Veterans Day to thank the men and women who have served. If that's you, we're grateful, not just today, but every day.",
    "From all of us at {{ companyName }}, thank you for your service.",
  ],
  signoff: "With gratitude,",
};

const thanksgivingDay0: EmailContentPreset = {
  key: "content.thanksgiving.day-0",
  category: "Seasonal",
  series: "thanksgiving",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Thanksgiving",
  description: "Heartfelt thank-you note for Thanksgiving.",
  subject: "Thank you this Thanksgiving, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "As Thanksgiving comes around, I wanted to take a minute to say thank you. Being trusted to look after your insurance isn't something we take for granted, and we're genuinely glad to have you as a customer.",
    "We hope you have a warm, full table and a peaceful day. From all of us at {{ companyName }}, happy Thanksgiving.",
  ],
  signoff: "With gratitude,",
};

const addHomeDay0: EmailContentPreset = {
  key: "content.add-home.day-0",
  category: "Bundle",
  series: "add-home",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Add Home Insurance",
  description:
    "Bundle pitch for auto-only customers. Invites a no-obligation home quote.",
  subject: "A quick thought about your home, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "You've been with {{ companyName }} for your auto, and we appreciate it. I wanted to send a quick note because something most of our auto customers don't realize: when you also have your home insurance with us, the auto premium typically drops too. Most folks save a few hundred dollars a year just from bundling.",
    "If you have a few minutes, I'd be happy to quote your home, no obligation, no pressure. Just a real number you can compare to what you're paying now.",
    "Reply to this email or call me at {{ agentPhone }} and we'll set it up.",
  ],
  signoff: "Talk soon,",
};

const referralAskDay0: EmailContentPreset = {
  key: "content.referral-ask.day-0",
  category: "Referral",
  series: "referral-ask",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Referral Ask",
  description:
    "Warm, no-script ask to existing customers for friend/family referrals.",
  subject: "A small ask, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "You've been a great customer, and honestly, the best compliment we can get is when one of our customers sends a friend or family member our way.",
    "If you know anyone shopping insurance, like a coworker, a neighbor, or a relative, and you think we'd take good care of them, would you let them know about us? I'd be grateful, and so would they.",
    "No script, no card to hand out. Just a name and a number, or send them my way at {{ agentPhone }}. We'll handle the rest.",
  ],
  signoff: "Thanks again,",
};

const saveCancellationDay0: EmailContentPreset = {
  key: "content.save-cancellation.day-0",
  category: "Win Back",
  series: "save-cancellation",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Save the Cancellation",
  description:
    "Last-chance personal note fired the same day a customer requests cancellation.",
  subject: "Before you go, {{ firstName }}, can we talk?",
  greeting: "{{ firstName }},",
  paragraphs: [
    "I just saw the cancellation request come through, and I wanted to reach out personally before it processes. We've taken care of your insurance for a while now, and I'd hate to see you go without at least having a quick conversation first.",
    "If something changed, whether it's the price, the service, or a claim that didn't go the way it should have, I'd like the chance to make it right. Even if you've already signed somewhere else, a 5-minute call could save you money or catch something the new policy isn't covering.",
    "Call me at {{ agentPhone }} or reply to this email. I'll make time today.",
  ],
  signoff: "Hoping to hear from you,",
};

const quoteNeverBoughtDay0: EmailContentPreset = {
  key: "content.quote-never-bought.day-0",
  category: "Win Back",
  series: "quote-never-bought",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Quote But Never Bought",
  description:
    "Follow-up for prospects who got a quote but never bound. Soft re-engagement.",
  subject: "Still shopping, {{ firstName }}?",
  greeting: "{{ firstName }},",
  paragraphs: [
    "A little while back you got a quote from us, and we never heard back. No pressure either way. I just wanted to check in.",
    "If you went with someone else, that's completely fine. But if you're still looking, or if the rate you got somewhere else didn't end up being what they promised at the start, I'd be happy to take another look. Rates change, and we can always re-quote with anything that's different since last time.",
    "Reply or call me at {{ agentPhone }} whenever it works for you.",
  ],
  signoff: "Best,",
};

const christmasDay0: EmailContentPreset = {
  key: "content.christmas.day-0",
  category: "Seasonal",
  series: "christmas",
  position: 1,
  dayOffset: 0,
  cadenceLabel: "Immediately",
  name: "Christmas",
  description: "Warm Christmas note thanking the customer for their year.",
  subject: "Merry Christmas from {{ companyName }}, {{ firstName }}",
  greeting: "{{ firstName }},",
  paragraphs: [
    "Wishing you and your family a Merry Christmas from everyone at {{ companyName }}.",
    "Whatever the day brings, a quiet morning, a packed house, or kids tearing through wrapping paper, we hope it's a warm one. Thank you for letting us be part of your year.",
  ],
  signoff: "Merry Christmas,",
};

// ---------------------------------------------------------------------------
// Registry.
// ---------------------------------------------------------------------------

export const EMAIL_CONTENT_PRESETS: EmailContentPreset[] = [
  // Welcome
  welcomeDay0,
  welcomeDay8,
  welcomeDay15,
  welcomeDay30,
  // EFS Welcome
  efsWelcomeDay0,
  efsWelcomeDay8,
  efsWelcomeDay15,
  efsWelcomeDay30,
  // X-Date
  xdateDay60,
  xdateDay45,
  xdateDay30,
  xdateDay20,
  xdateDay15,
  xdateDay10,
  // CPR
  cprFirst,
  cprSecond,
  cprThird,
  // New Quote (online)
  nbOnlineDay0,
  nbOnlineDay3,
  nbOnlineDay5,
  // New Quote (phone)
  nbPhoneDay0,
  nbPhoneDay3,
  nbPhoneDay5,
  // Thank You — existing customers
  thankYouDay0,
  thankYouDay8,
  thankYouDay15,
  // Endorsement
  endorsementGeneric,
  endorsementAllstate,
  // Claims
  claimsFiled,
  claimsFollowup,
  // Win Back
  winbackDay0,
  winbackDay3,
  winbackDay5,
  saveCancellationDay0,
  quoteNeverBoughtDay0,
  // Existing-customer growth
  addHomeDay0,
  referralAskDay0,
  // Birthday
  birthdayDay0,
  // Seasonal
  holidayGreetingDay0,
  hurricanePrepDay0,
  taxSeasonDay0,
  backToSchoolDay0,
  newYearDay0,
  valentinesDay0,
  stPatricksDay0,
  easterDay0,
  mothersDay0,
  memorialDay0,
  fathersDay0,
  independenceDay0,
  laborDay0,
  halloweenDay0,
  veteransDay0,
  thanksgivingDay0,
  christmasDay0,
];

export const EMAIL_CONTENT_CATEGORIES = Array.from(
  new Set(EMAIL_CONTENT_PRESETS.map((p) => p.category))
);

/** Series metadata for the "Create whole sequence" flow (used in a later step). */
export interface EmailContentSeries {
  series: string;
  category: string;
  name: string;
  description: string;
  members: EmailContentPreset[];
}

export const EMAIL_CONTENT_SERIES: EmailContentSeries[] = [
  {
    series: "welcome",
    category: "Welcome",
    name: "Welcome Auto-Responder (4 emails, 30 days)",
    description:
      "Standard new-customer welcome cadence: immediate, day 8, day 15, day 30.",
    members: [welcomeDay0, welcomeDay8, welcomeDay15, welcomeDay30],
  },
  {
    series: "efs-welcome",
    category: "Welcome",
    name: "EFS Intro Welcome (4 emails, 30 days)",
    description:
      "Same welcome cadence with a Financial Specialist handoff on day 8.",
    members: [efsWelcomeDay0, efsWelcomeDay8, efsWelcomeDay15, efsWelcomeDay30],
  },
  {
    series: "xdate",
    category: "X-Date",
    name: "X-Date Renewal Drip (6 emails, 60 days)",
    description:
      "Renewal-window cadence starting 60 days prior: 60 / 45 / 30 / 20 / 15 / 10 days out.",
    members: [
      xdateDay60,
      xdateDay45,
      xdateDay30,
      xdateDay20,
      xdateDay15,
      xdateDay10,
    ],
  },
  {
    series: "cpr",
    category: "CPR",
    name: "CPR Campaign (3 emails, 20 days)",
    description: "Customer Protection Review push: immediate, day 10, day 20.",
    members: [cprFirst, cprSecond, cprThird],
  },
  {
    series: "nb-online",
    category: "New Quote",
    name: "New Business: Online Quote (3 emails, 5 days)",
    description: "Follow-up after a customer submits an online quote request.",
    members: [nbOnlineDay0, nbOnlineDay3, nbOnlineDay5],
  },
  {
    series: "nb-phone",
    category: "New Quote",
    name: "New Business: Phone Quote (3 emails, 5 days)",
    description: "Follow-up after a phone-conversation quote.",
    members: [nbPhoneDay0, nbPhoneDay3, nbPhoneDay5],
  },
  {
    series: "thank-you-existing",
    category: "Thank You",
    name: "Thank You Kit: Existing Customers (3 emails, 15 days)",
    description:
      "Cross-sell thank-you cadence for existing customers buying more coverage.",
    members: [thankYouDay0, thankYouDay8, thankYouDay15],
  },
  {
    series: "claims",
    category: "Claims",
    name: "Claims Follow-Up (2 emails, 7 days)",
    description: "Sympathetic acknowledgment + week-later check-in.",
    members: [claimsFiled, claimsFollowup],
  },
  {
    series: "winback",
    category: "Win Back",
    name: "Win Back (3 emails, 5 days)",
    description: "Lapsed-customer reactivation: immediate, day 3, day 5.",
    members: [winbackDay0, winbackDay3, winbackDay5],
  },
  {
    series: "save-cancellation",
    category: "Win Back",
    name: "Save the Cancellation (1 email)",
    description:
      "Last-chance personal note fired the same day a customer requests cancellation.",
    members: [saveCancellationDay0],
  },
  {
    series: "quote-never-bought",
    category: "Win Back",
    name: "Quote But Never Bought (1 email)",
    description: "Follow-up for prospects who got a quote but never bound.",
    members: [quoteNeverBoughtDay0],
  },
  {
    series: "add-home",
    category: "Bundle",
    name: "Add Home Insurance (1 email)",
    description:
      "Bundle pitch for auto-only customers. Invites a no-obligation home quote.",
    members: [addHomeDay0],
  },
  {
    series: "referral-ask",
    category: "Referral",
    name: "Referral Ask (1 email)",
    description:
      "Warm, no-script ask to existing customers for friend/family referrals.",
    members: [referralAskDay0],
  },
  {
    series: "birthday",
    category: "Birthday",
    name: "Birthday Greeting (1 email)",
    description:
      "Single warm, no-ask birthday note fired on each customer's birthday.",
    members: [birthdayDay0],
  },
  {
    series: "holiday-greeting",
    category: "Seasonal",
    name: "Holiday Greeting (1 email)",
    description:
      "Year-end thank-you note. Works for Thanksgiving, Christmas, and New Year.",
    members: [holidayGreetingDay0],
  },
  {
    series: "hurricane-prep",
    category: "Seasonal",
    name: "Severe-Weather Prep (1 email)",
    description:
      "Storm-season reminder with claim hotline. Fires once at the start of weather season.",
    members: [hurricanePrepDay0],
  },
  {
    series: "tax-season",
    category: "Seasonal",
    name: "Tax-Season Check-In (1 email)",
    description:
      "Light-touch outreach during W-2/1099 season with a hook to review life and disability coverage.",
    members: [taxSeasonDay0],
  },
  {
    series: "back-to-school",
    category: "Seasonal",
    name: "Back-to-School Coverage Check (1 email)",
    description:
      "August reminder covering teen drivers, dorm renters insurance, and college-student auto re-rates.",
    members: [backToSchoolDay0],
  },
  {
    series: "new-year",
    category: "Seasonal",
    name: "New Year (1 email)",
    description: "Warm new-year note with a soft hook to review coverage.",
    members: [newYearDay0],
  },
  {
    series: "valentines",
    category: "Seasonal",
    name: "Valentine's Day (1 email)",
    description: "Short, warm Valentine's Day note with no ask.",
    members: [valentinesDay0],
  },
  {
    series: "st-patricks",
    category: "Seasonal",
    name: "St. Patrick's Day (1 email)",
    description:
      "Friendly St. Patrick's Day note with a safe-ride-home reminder.",
    members: [stPatricksDay0],
  },
  {
    series: "easter",
    category: "Seasonal",
    name: "Easter (1 email)",
    description: "Brief, warm Easter note.",
    members: [easterDay0],
  },
  {
    series: "mothers-day",
    category: "Seasonal",
    name: "Mother's Day (1 email)",
    description: "Warm Mother's Day note.",
    members: [mothersDay0],
  },
  {
    series: "memorial-day",
    category: "Seasonal",
    name: "Memorial Day (1 email)",
    description:
      "Memorial Day remembrance plus a safe-travels note for the long weekend.",
    members: [memorialDay0],
  },
  {
    series: "fathers-day",
    category: "Seasonal",
    name: "Father's Day (1 email)",
    description: "Warm Father's Day note.",
    members: [fathersDay0],
  },
  {
    series: "independence-day",
    category: "Seasonal",
    name: "Independence Day (1 email)",
    description: "Friendly 4th of July note with a safe-celebration reminder.",
    members: [independenceDay0],
  },
  {
    series: "labor-day",
    category: "Seasonal",
    name: "Labor Day (1 email)",
    description: "Brief end-of-summer Labor Day note.",
    members: [laborDay0],
  },
  {
    series: "halloween",
    category: "Seasonal",
    name: "Halloween (1 email)",
    description: "Fun Halloween note with a drive-slow reminder.",
    members: [halloweenDay0],
  },
  {
    series: "veterans-day",
    category: "Seasonal",
    name: "Veterans Day (1 email)",
    description: "Thank-you note for Veterans Day.",
    members: [veteransDay0],
  },
  {
    series: "thanksgiving",
    category: "Seasonal",
    name: "Thanksgiving (1 email)",
    description: "Heartfelt thank-you note for Thanksgiving.",
    members: [thanksgivingDay0],
  },
  {
    series: "christmas",
    category: "Seasonal",
    name: "Christmas (1 email)",
    description: "Warm Christmas note thanking the customer for their year.",
    members: [christmasDay0],
  },
];
